import { Spacing, Text } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { duplicatesTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { useDuplicatesAnalytic } from 'reactApp/hooks/duplicates/useDuplicatesAnalytic';
import { removeDuplicatesAllWithLimitStart } from 'reactApp/modules/duplicates/duplicates.actions';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { duplicatesToDeletePlural } from 'reactApp/utils/pluralHelpers';

import styles from './DuplicatesRemoveLimitWarningModal.css';

interface Props {
    onClose?: () => void;
    count: number;
}

const name = 'dp-rm-with-lim-modal';

const handleDismiss = () => {
    sendXray([name, 'close']);
};

export const DuplicatesRemoveLimitWarningModal: FC<Props> = memo(function DuplicatesRemoveLimitWarningModal(props: Props) {
    const { onClose, count } = props;

    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const analyticData = useDuplicatesAnalytic();
    const dispatch = useDispatch();

    const onClickBuy = useTariffBuy({
        paySource: duplicatesTariffsModal.paySource,
    });

    const handleClose = useCallback(() => {
        onClose?.();
        dispatch(removeDuplicatesAllWithLimitStart({ count, showPromo: false }));
    }, [count, dispatch, onClose]);

    const handleShow = useCallback(() => {
        sendXray([name, 'show']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-show',
            modal_type: 'remove-all-with-limit',
            ...analyticData,
        });
    }, [analyticData]);

    const handleClick = useCallback(() => {
        sendXray([name, 'click']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-click',
            modal_type: 'remove-all-with-limit',
            action_type: 'try-promo',
            ...analyticData,
        });

        handleClose();
        dispatch(
            openBuyDisableAds({
                title: duplicatesTariffsModal.buyTitle,
                tariffsNumber: duplicatesTariffsModal.tariffsNumber,
                activeTab: duplicatesTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: duplicatesTariffsModal.actionShow,
                actionClick: duplicatesTariffsModal.actionClick,
            })
        );
    }, [analyticData, handleClose, dispatch, onClickBuy]);

    return (
        <WhatsNewDialog
            imageUrl={`${cdnPackagesUrl}/static/cloud/promo/duplicates/promo-modal-${isDarkTheme ? 'dark' : 'light'}.png`}
            imageWrapperClassName={styles.imageWrapper}
            contentClassName={styles.content}
            title={`Удалятся дубликаты только ${count}\u00A0${duplicatesToDeletePlural(count)}`}
            content={
                <>
                    <Spacing size={16} />
                    <Text data-qa-id="text">
                        Можно удалить все&nbsp;&mdash; с&nbsp;подпиской Mail Space. И&nbsp;место освободится, и&nbsp;фото не&nbsp;придётся
                        разбирать вручную
                    </Text>
                    <Spacing size={32} />
                </>
            }
            primaryButtonText="Попробовать подписку"
            closeAfterClick
            onClick={handleClick}
            onClose={handleClose}
            onShow={handleShow}
            onDismiss={handleDismiss}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="small"
            qaId="duplicates-remove-with-limit-modal"
            isRebranding
        />
    );
});

DuplicatesRemoveLimitWarningModal.displayName = 'DuplicatesRemoveLimitWarningModal';
